import { FaBook, FaPlay, FaRedo } from "react-icons/fa";
import { PiMathOperationsFill } from "react-icons/pi";

const ConfirmModal = ({ isOpen, onConfirm, onCancel }) => {
  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black/40 backdrop-blur-sm z-50">
      <div className="bg-white rounded-2xl shadow-xl border border-slate-100 p-6 w-80 mx-4">
        <div className="w-12 h-12 rounded-2xl bg-blue-600 flex items-center justify-center mx-auto mb-4">
          <FaPlay className="text-white text-sm ml-0.5" />
        </div>
        <h3 className="text-slate-800 text-base font-bold text-center mb-1">Start Mock SAT?</h3>
        <p className="text-slate-500 text-sm text-center mb-5 leading-relaxed">
          This is a full-length timed test. Make sure you have enough time to complete it.
        </p>
        <div className="flex gap-3">
          <button
            onClick={onCancel}
            className="flex-1 py-2.5 rounded-xl text-sm font-semibold text-slate-600 bg-slate-100 hover:bg-slate-200 transition-all duration-200"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            className="flex-1 py-2.5 rounded-xl text-sm font-semibold text-white bg-blue-600 hover:bg-blue-500 transition-all duration-200 shadow-sm"
          >
            Start Now
          </button>
        </div>
      </div>
    </div>
  );
};

const ScoreBar = ({ score, max = 800, color = "blue" }) => {
  const pct = score ? Math.min((score / max) * 100, 100) : 0;
  const barColor = color === "blue" ? "bg-blue-500" : "bg-violet-500";
  return (
    <div className="h-1 w-full bg-slate-100 rounded-full overflow-hidden">
      <div className={`h-full ${barColor} rounded-full transition-all duration-500`} style={{ width: `${pct}%` }} />
    </div>
  );
};

const MockSATSection = ({
  mockCardData,
  handleCardClick,
  getMockScoresForCard,
  showConfirmModal,
  handleConfirm,
  handleCancel,
}) => {
  const completedCount = mockCardData.filter(num => !!getMockScoresForCard(num)).length;

  return (
    <section className="w-full">
      {/* Progress summary */}
      <div className="flex items-center justify-between mb-3">
        <p className="text-xs text-slate-500 font-medium">
          <span className="font-bold text-slate-700">{completedCount}</span> / {mockCardData.length} completed
        </p>
        <div className="flex gap-1">
          {mockCardData.map(num => {
            const done = !!getMockScoresForCard(num);
            return (
              <div
                key={num}
                className={`w-5 h-1.5 rounded-full transition-colors ${done ? 'bg-blue-500' : 'bg-slate-200'}`}
              />
            );
          })}
        </div>
      </div>

      {/* Cards grid — all 6 visible */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        {mockCardData.map((num) => {
          const scores = getMockScoresForCard(num);
          const done = !!scores;
          const isNext = !done && mockCardData.slice(0, num - 1).every(n => !!getMockScoresForCard(n));

          return (
            <button
              key={`mock-sat-${num}`}
              onClick={() => handleCardClick(num)}
              className={`group relative flex flex-col text-left rounded-2xl border transition-all duration-200 overflow-hidden focus:outline-none
                ${done
                  ? 'bg-white border-slate-200 hover:border-blue-300 hover:shadow-md hover:shadow-blue-100'
                  : isNext
                  ? 'bg-blue-600 border-blue-600 hover:bg-blue-500 hover:shadow-lg hover:shadow-blue-200'
                  : 'bg-white border-slate-200 hover:border-slate-300 hover:shadow-sm'
                }`}
            >
              {/* Top label */}
              <div className="px-3 pt-3 pb-2">
                <div className="flex items-center justify-between mb-0.5">
                  <span className={`text-[9px] font-bold uppercase tracking-widest ${done ? 'text-slate-400' : isNext ? 'text-blue-200' : 'text-slate-400'}`}>
                    Mock
                  </span>
                  {done && (
                    <span className="w-4 h-4 rounded-full bg-emerald-100 flex items-center justify-center">
                      <svg className="w-2.5 h-2.5 text-emerald-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                      </svg>
                    </span>
                  )}
                  {isNext && !done && (
                    <span className="text-[9px] font-bold text-blue-200 bg-blue-500/50 px-1.5 py-0.5 rounded-full">Next</span>
                  )}
                </div>
                <p className={`text-sm font-black leading-tight ${done ? 'text-slate-800' : isNext ? 'text-white' : 'text-slate-700'}`}>
                  SAT {num}
                </p>
              </div>

              {/* Score area */}
              <div className="flex-1 px-3 pb-3">
                {done ? (
                  <>
                    {/* Total score */}
                    <div className="mb-2">
                      <p className="text-[9px] text-slate-400 font-semibold mb-0.5">Total</p>
                      <p className="text-lg font-black text-blue-600 leading-none tabular-nums">
                        {scores.total}
                        <span className="text-[10px] font-normal text-slate-300 ml-0.5">/1600</span>
                      </p>
                    </div>

                    {/* Sub scores */}
                    <div className="space-y-1.5">
                      <div>
                        <div className="flex items-center justify-between mb-0.5">
                          <div className="flex items-center gap-0.5">
                            <FaBook className="text-[8px] text-blue-400" />
                            <span className="text-[9px] text-blue-500 font-semibold">R&W</span>
                          </div>
                          <span className="text-[10px] font-bold text-slate-700 tabular-nums">{scores.eng}</span>
                        </div>
                        <ScoreBar score={scores.eng} color="blue" />
                      </div>
                      <div>
                        <div className="flex items-center justify-between mb-0.5">
                          <div className="flex items-center gap-0.5">
                            <PiMathOperationsFill className="text-[8px] text-violet-400" />
                            <span className="text-[9px] text-violet-500 font-semibold">Math</span>
                          </div>
                          <span className="text-[10px] font-bold text-slate-700 tabular-nums">{scores.math}</span>
                        </div>
                        <ScoreBar score={scores.math} color="violet" />
                      </div>
                    </div>

                    {/* Retake CTA */}
                    <div className="mt-3 flex items-center gap-1.5 text-blue-600 group-hover:text-blue-500 transition-colors">
                      <FaRedo className="text-[9px]" />
                      <span className="text-[10px] font-bold">Retake</span>
                    </div>
                  </>
                ) : (
                  <>
                    {/* Not taken */}
                    <div className="space-y-1 mb-3">
                      <div className={`h-2 rounded w-full ${isNext ? 'bg-white/25' : 'bg-slate-100'}`} />
                      <div className={`h-2 rounded w-3/4 ${isNext ? 'bg-white/20' : 'bg-slate-100'}`} />
                      <div className={`h-2 rounded w-1/2 ${isNext ? 'bg-white/15' : 'bg-slate-100'}`} />
                    </div>
                    <div className={`flex items-center gap-1.5 ${isNext ? 'text-white' : 'text-slate-400'} transition-colors`}>
                      <FaPlay className="text-[9px]" />
                      <span className="text-[10px] font-bold">{isNext ? 'Start Test' : 'Not started'}</span>
                    </div>
                  </>
                )}
              </div>
            </button>
          );
        })}
      </div>

      {showConfirmModal && (
        <ConfirmModal
          isOpen={showConfirmModal}
          onConfirm={handleConfirm}
          onCancel={handleCancel}
        />
      )}
    </section>
  );
};

export default MockSATSection;
